import { haversineKm } from "./cost.js";

const CELL_DEGREES = 0.025;
const CELL_PADDING = 0.02;
const cell = (value) => Math.floor(value / CELL_DEGREES);
const cellKey = (x, y) => `${x}:${y}`;

function routeSegments(geometry) {
  if (!Array.isArray(geometry)) return [];
  const segments = [];
  let travelledKm = 0;
  for (let index = 1; index < geometry.length; index += 1) {
    const start = geometry[index - 1];
    const end = geometry[index];
    if (![start?.[0], start?.[1], end?.[0], end?.[1]].every(Number.isFinite)) continue;
    const lengthKm = haversineKm({ lat: start[1], lng: start[0] }, { lat: end[1], lng: end[0] });
    segments.push({ start, end, travelledKm, lengthKm });
    travelledKm += lengthKm;
  }
  return segments;
}

function projectOnSegments(point, segments) {
  if (!Number.isFinite(point?.lat) || !Number.isFinite(point?.lng)) return null;
  let closest = null;
  const latScale = 110.574;
  const lngScale = 111.32 * Math.cos((point.lat * Math.PI) / 180);
  for (const { start, end, travelledKm, lengthKm } of segments) {
    const ax = (start[0] - point.lng) * lngScale;
    const ay = (start[1] - point.lat) * latScale;
    const dx = (end[0] - start[0]) * lngScale;
    const dy = (end[1] - start[1]) * latScale;
    const ratio = dx * dx + dy * dy ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / (dx * dx + dy * dy))) : 0;
    const offsetKm = Math.hypot(ax + ratio * dx, ay + ratio * dy);
    const distanceKm = travelledKm + ratio * lengthKm;
    if (!closest || offsetKm < closest.offsetKm) closest = { distanceKm, offsetKm };
  }
  return closest;
}

export function projectPointToRoute(point, geometry) {
  return projectOnSegments(point, routeSegments(geometry));
}

export function camerasAlongRoute(cameras, geometry, maxOffsetKm = 1) {
  if (!Array.isArray(cameras) || !Array.isArray(geometry) || geometry.length < 2) return [];
  const segments = routeSegments(geometry);
  const grid = new Map();
  const padding = Math.max(CELL_PADDING, maxOffsetKm / 75);
  for (const segment of segments) {
    const minX = cell(Math.min(segment.start[0], segment.end[0]) - padding);
    const maxX = cell(Math.max(segment.start[0], segment.end[0]) + padding);
    const minY = cell(Math.min(segment.start[1], segment.end[1]) - padding);
    const maxY = cell(Math.max(segment.start[1], segment.end[1]) + padding);
    for (let x = minX; x <= maxX; x += 1) for (let y = minY; y <= maxY; y += 1) {
      const key = cellKey(x, y);
      if (!grid.has(key)) grid.set(key, []);
      grid.get(key).push(segment);
    }
  }
  return cameras.flatMap((camera) => {
    const nearby = grid.get(cellKey(cell(camera.lng), cell(camera.lat))) || [];
    const match = projectOnSegments(camera, nearby);
    return match && match.offsetKm <= maxOffsetKm ? [{ ...camera, ...match }] : [];
  }).sort((a, b) => a.distanceKm - b.distanceKm || String(a.id).localeCompare(String(b.id)));
}
